import {
  effect,
  inject,
  Injectable,
  Injector,
  provideAppInitializer,
} from '@angular/core';
import { ConfigService } from '@rxap/config';
import { RXAP_ENVIRONMENT } from '@rxap/environment';
import Userback, { UserbackWidget } from '@userback/widget';
import { confidentialityStore } from './confidentiality/confidentiality.store';

@Injectable({ providedIn: 'root' })
export class UserbackService {
  #instance: UserbackWidget | null = null;

  private readonly config = inject(ConfigService);
  private readonly environment = inject(RXAP_ENVIRONMENT);
  private readonly store = inject(confidentialityStore);
  private readonly injector = inject(Injector);

  async init() {
    const token = this.config.get('userback.token');

    if (!token) {
      console.warn('Userback token is missing, skipping initialization');
      return;
    }

    if (this.config.get('userback.disabled')) {
      console.debug('Userback is disabled, skipping initialization');
      return;
    }

    try {
      this.#instance = await Userback(token, {
        is_live:
          this.environment.production && this.environment.tier === 'production',
        custom_data: this.environment,
      });
    } catch (e: any) {
      console.error(`Failed to initialize Userback: ${e.message}`);
    }
    this.setProfile();
    return this.#instance;
  }

  /**
   * Reactively forward the signed-in user's identity to Userback. The viewer
   * has no RXAP user-profile source; its only identity source is the
   * confidentiality store's `userInfo` signal (populated from the oauth2-proxy
   * `/oauth2/userinfo` endpoint, and only in confidential mode). Mirrors the
   * effect-based pattern used in init-posthog.ts. Created after `init()` so the
   * widget instance already exists when the effect first reads `userInfo()`.
   */
  setProfile() {
    effect(
      () => {
        const userInfo = this.store.userInfo();
        if (!userInfo?.email) {
          return;
        }
        const email = userInfo.email;
        const name =
          userInfo['name'] ||
          userInfo['preferred_username'] ||
          userInfo['displayName'] ||
          email;
        const userId = userInfo['sub'] || email;
        this.identify(userId, name, email);
      },
      { injector: this.injector }
    );
  }

  setEmail(email: string) {
    if (!this.#instance) {
      console.debug(
        'Userback instance is not initialized, cannot set user data'
      );
      return;
    }
    this.#instance.setEmail(email);
  }

  setName(name: string) {
    if (!this.#instance) {
      console.debug(
        'Userback instance is not initialized, cannot set user data'
      );
      return;
    }
    this.#instance.setName(name);
  }

  identify(userId: string, name: string, email: string, ...args: any[]) {
    if (!this.#instance) {
      console.debug(
        'Userback instance is not initialized, cannot identify user'
      );
      return;
    }
    this.#instance.identify(userId, {
      name,
      email,
      ...args,
    });
  }
}

export function provideUserback() {
  return [
    provideAppInitializer(async () => {
      const userback = inject(UserbackService);
      await userback.init();
    })
  ]
}
